import { hasRole, isPlatformAdmin, type Role, type Session } from '@/shared/api';

/**
 * Wat deze sessie met keuringen mag. De API beslist - een verboden actie geeft
 * 403 - maar een knop die gegarandeerd faalt hoort er niet te staan.
 *
 * De permissies uit de sessie zijn leidend (`orders.manage`, `orders.assign`,
 * `invoices.view`); ontbreken ze (een oudere sessie), dan volgt dit de
 * rollentabel uit `Identity\Enums\Role`.
 */
export interface OrderAccess {
    /** Keuring aanmaken, status wijzigen, annuleren. */
    manage: boolean;
    /**
     * Het adres van het object wijzigen: wie keuringen beheert, en platformbeheer, dat het
     * ook na verzending nog mag (de API beslist over dat slot).
     */
    changeAddress: boolean;
    /** Inspecteur toewijzen en inplannen. */
    assign: boolean;
    /** Prijs, vergoeding en marge zien. */
    money: boolean;
    /** Facturatie openen. */
    invoices: boolean;
    /** Zelf een keuring starten en de opname invullen. */
    perform: boolean;
    /** Wie deze sessie is; zonder uuid (een oude sessie) telt geen keuring als "van mij". */
    userUuid: string | null;
    /**
     * Werkt deze sessie op locatie? De rol, niet de permissie: een eigenaar mág opnemen,
     * maar zonder de rol inspecteur staat de keuring niet in zijn Mijn dag. Wie zelf start
     * krijgt daarom eerst de vraag of hij ook als inspecteur wil werken.
     */
    field: boolean;
}

const OFFICE: readonly Role[] = ['owner'];

const FIELD: readonly Role[] = ['inspector'];

export function orderAccessFor(
    session: Pick<Session, 'user' | 'roles' | 'permissions'> | null,
): OrderAccess {
    const permissions = session?.permissions ?? [];
    const stated = permissions.length > 0;
    const may = (permission: string) =>
        stated ? permissions.includes(permission) : hasRole(session, OFFICE);

    return {
        manage: may('orders.manage'),
        changeAddress: may('orders.manage') || isPlatformAdmin(session),
        assign: may('orders.assign'),
        money: hasRole(session, OFFICE),
        invoices: may('invoices.view'),
        /* Opnemen doet kantoor én de inspecteur, dus de terugval op rollen ook. */
        perform: stated
            ? permissions.includes('inspections.perform')
            : hasRole(session, [...OFFICE, ...FIELD]),
        userUuid: session?.user.uuid ?? null,
        /* `hasRole` laat een platformbeheerder overal door; die kijkt mee, hij keurt niet. */
        field: (session?.roles ?? []).some((role) => FIELD.includes(role)),
    };
}
